import type { MenuOption } from "@dealer-center/core-components";
import { isVinValid, useToaster } from "@dealer-center/core-components";
import { MessagePage } from "@dealer-center/core-custom-components";

import { PageTemplate } from "@dealer-center/core-navbar";
import {
  useAccountFavourites,
  useAccountHistory,
  useGetAccount,
  useGetAccountId,
  useGetAccountNames
} from "@dealer-center/ddc-api-account-fetch";
import type { AccountEntity } from "@dealer-center/ddc-api-account-interface";
import { useNavbarItems } from "@dealer-center/ddc-api-commoncontainer-fetch";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useNavigateToAccount } from "@dealer-center/ddc-core-utils";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function CoreNavbar({ children }: { children: React.ReactNode }) {
  const {
    data: userData,
    isLoading: isUserLoading,
    error: userError
  } = useGetUser();
  const {
    error: navbarError,
    isLoading: isNavbarLoading,
    data: navbarData
  } = useNavbarItems();
  const [accountToSearchFor, setAccountToSearchFor] = useState<
    string | undefined
  >();
  const { toastError } = useToaster();
  const { data: foundAccount } = useGetAccount({
    accountId: accountToSearchFor
  });

  const currentAccount = useGetAccountId();
  const navigate = useNavigate();
  const accountNavigator = useNavigateToAccount();
  useEffect(() => {
    if (foundAccount && foundAccount.id === accountToSearchFor) {
      accountNavigator.navigateToAccount(foundAccount.id);
    }
  }, [accountNavigator, accountToSearchFor, foundAccount]);
  useEffect(() => {
    if (userError) {
      toastError("Error loading user data" + userError);
    }
  }, [userError]);
  useEffect(() => {
    if (navbarError) {
      toastError("Error loading navbar items" + navbarError);
    }
  }, [navbarError]);

  const availableAccounts: MenuOption[] = useGetAvailableAccounts();

  const accountStatus = getAccountStatusForNavbar(foundAccount);
  if (isUserLoading)
    return (
      <MessagePage
        actionName={"Loading user state"}
        message="Loading user information"
      />
    );
  if (isNavbarLoading)
    return (
      <MessagePage
        actionName={"Loading navbar items"}
        message="Loading navbar items"
      />
    );

  return (
    <PageTemplate
      availableAccounts={availableAccounts}
      hasBridgeBar={true}
      linksMenu={navbarData?.linksMenu}
      nav={navbarData?.usermenu.nav}
      selectAccount={account => {
        accountNavigator.navigateToAccount(account);
      }}
      selectedAccount={currentAccount}
      selectedAccountStatus={accountStatus}
      user={{
        name: userData?.first + " " + userData?.last
      }}
      onSearch={value => {
        if (isVinValid(value.inputValue)) {
          navigate("/vin-decode/US/" + value.inputValue + "/");
          return;
        }
        setAccountToSearchFor(value.inputValue);
      }}
    >
      {children}
    </PageTemplate>
  );

  function getAccountStatusForNavbar(
    foundAccount: AccountEntity | undefined
  ): string | undefined {
    if (!foundAccount) return undefined;
    const status = foundAccount.status;
    if (status.toLowerCase() === "active") return undefined;
    return status;
  }

  function useGetAvailableAccounts() {
    const { accountFavorites } = useAccountFavourites();
    const { accountHistory } = useAccountHistory();
    const availableAccountsWithoutName: MenuOption[] =
      generateAvailableAccounts(accountFavorites, accountHistory);
    const accountNamesToGet: string[] = [
      currentAccount || "",
      ...(availableAccountsWithoutName
        ? availableAccountsWithoutName.map(
            account => account.value?.toString() || ""
          )
        : [])
    ];
    const { data: accountNames, isLoading: isAccountNamesLoading } =
      useGetAccountNames(accountNamesToGet);
    const availableAccounts: MenuOption[] = isAccountNamesLoading
      ? availableAccountsWithoutName
      : accountNames?.map(account => ({
          label: account.accountName,
          value: account.accountId
        })) || availableAccountsWithoutName;
    return availableAccounts;
  }
  function generateAvailableAccounts(
    accountFavorites?: readonly string[],
    accountHistory?: readonly string[]
  ) {
    const availableAccounts: MenuOption[] =
      accountFavorites?.map(favorite => ({
        label: favorite,
        value: favorite
      })) || [];
    accountHistory?.forEach(account => {
      if (!availableAccounts.find(available => available.label === account)) {
        availableAccounts.push({ label: account, value: account });
      }
    });
    return availableAccounts;
  }
}

export default CoreNavbar;
